<script setup lang="ts">
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import { VBadge, VButton, VIcon, VSection, VSkeletonBar } from '@/modules/shared/components'
import { safeFormat } from '@/modules/shared/utils/date'
import DeliveryStatus from './DeliveryStatus.vue'
import type { Message } from '../types'

const props = defineProps<{
  message: Message | null
  skeleton: boolean
  isAdmin: boolean
  isSender: boolean
}>()

const sanitizedBody = computed(() => {
  if (!props.message?.body) return ''
  return DOMPurify.sanitize(props.message.body)
})

const emit = defineEmits<{
  delete: [id: number]
}>()

const onDelete = (id: number) => {
  if (!window.confirm('Are you sure you want to delete this message?')) return
  emit('delete', id)
}
</script>

<template>
  <div v-if="skeleton" class="space-y-4">
    <VSkeletonBar />
    <VSkeletonBar />
    <VSkeletonBar />
  </div>

  <div v-else-if="message" :class="isAdmin ? 'grid grid-cols-1 gap-8 lg:grid-cols-3' : ''">
    <!-- Left column: message content -->
    <div class="lg:col-span-2">
      <VSection label="Message Center">
        <template v-if="isAdmin || isSender" #actions>
          <VButton @click="onDelete(message.id)" size="sm" class="text-red-600 hover:text-red-700"> Delete </VButton>
        </template>

        <div class="space-y-5">
          <!-- Email-like header block -->
          <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <!-- From row -->
            <div class="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
              <div class="flex items-center">
                <span class="w-20 shrink-0 text-sm font-semibold text-gray-500">From</span>
                <span class="text-sm text-gray-700">{{ message.sender_name }}</span>
              </div>
              <span v-if="message.created_at" class="text-sm text-gray-400">
                {{ safeFormat(message.created_at, "MMM d, yyyy 'at' h:mm a") }}
              </span>
            </div>

            <!-- To row (admin only) -->
            <div v-if="isAdmin" class="flex items-start border-b border-gray-100 px-4 py-2.5">
              <span class="w-20 shrink-0 pt-0.5 text-sm font-semibold text-gray-500">To</span>
              <div class="flex flex-wrap gap-1.5">
                <VBadge v-for="receiver in message.message_receivers" :key="receiver.id" color="gray" size="xs">
                  {{ receiver.in_words }}
                </VBadge>
              </div>
            </div>

            <!-- Subject row -->
            <div class="flex items-center px-4 py-2.5">
              <span class="w-20 shrink-0 text-sm font-semibold text-gray-500">Subject</span>
              <span class="text-sm font-medium text-gray-900">{{ message.subject }}</span>
            </div>
          </div>

          <!-- Message body -->
          <div class="prose max-w-none pt-2 prose-a:font-normal prose-a:text-blue-600" v-html="sanitizedBody" />

          <!-- Attachments -->
          <div v-if="message.uploaded_files?.length" class="border-t border-gray-200 pt-4">
            <div class="mb-2 flex items-center gap-2">
              <VIcon name="Paperclip" class="h-4 w-4 text-gray-400" />
              <span class="text-sm font-medium text-gray-700">Attachments</span>
            </div>
            <ul class="ml-6 space-y-1">
              <li v-for="file in message.uploaded_files" :key="file.id">
                <a
                  :href="file.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-[#3b88af] hover:underline"
                >
                  {{ file.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </VSection>
    </div>

    <!-- Right column: delivery status sidebar (admin only) -->
    <div v-if="isAdmin" class="lg:col-span-1">
      <div class="sticky top-8">
        <VSection label="Status">
          <DeliveryStatus :logs="message.message_logs" :percentage-opened="message.percentage_opened" compact />
        </VSection>
      </div>
    </div>
  </div>
</template>
